import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../ThemeContext";
import { get, put } from "../api";
import { Card, EmptyState } from "../components/UI";
import T from "../i18n";
import { LANGUAGES } from "../i18n";

export default function Configuracao({ sub, lojaId, lang, setLang }) {
  const { theme: C, config: themeCfg, updateConfig, PRESETS } = useTheme();
  const _ = (k) => T(lang, k);
  const [loja, setLoja] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  const diasSemana = [_("segunda"), _("terca"), _("quarta"), _("quinta"), _("sexta"), _("sabado"), _("domingo")];
  const [horarios, setHorarios] = useState(diasSemana.map(d => ({ dia: d, aberto: true, abre: "08:00", fecha: "23:00" })));

  const [fDados, setFDados] = useState({ nome: "", telefone: "", email: "", endereco: "", cidade: "", estado: "", cnpj: "" });
  const [fApi, setFApi] = useState({ mp_access_token: "", mp_public_key: "", webhook_url: "" });
  const [fImp, setFImp] = useState({ tipo: "bluetooth", nome_dispositivo: "", largura_papel: "80", auto_imprimir: "1" });
  const [tiposPag, setTiposPag] = useState([
    { key: "pix", label: "PIX", ativo: true },
    { key: "credito", label: _("cartaoCredito"), ativo: true },
    { key: "debito", label: _("cartaoDebito"), ativo: true },
    { key: "dinheiro", label: _("dinheiro"), ativo: true },
    { key: "cashback", label: "Cashback", ativo: false },
    { key: "vale_refeicao", label: _("valeRefeicao"), ativo: false },
  ]);

  const notify = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 2800); };

  const fetchLoja = useCallback(async () => {
    try {
      const res = await get("lojas", { id: lojaId });
      if (res.dados && res.dados.length > 0) {
        const l = res.dados[0];
        setLoja(l);
        setFDados({ nome: l.nome || "", telefone: l.telefone || "", email: l.email || "", endereco: l.endereco || "", cidade: l.cidade || "", estado: l.estado || "", cnpj: l.cnpj || "" });
      }
    } catch (e) { console.error(e); }
  }, [lojaId]);

  useEffect(() => { fetchLoja(); }, [fetchLoja]);
  useEffect(() => { setHorarios(prev => prev.map((h, i) => ({ ...h, dia: diasSemana[i] }))); }, [lang]);

  const salvarDados = async () => {
    setSaving(true);
    try { await put("lojas", lojaId, fDados); notify(_("dadosAtualizados")); fetchLoja(); }
    catch (e) { notify(_("erroSalvar"), false); }
    finally { setSaving(false); }
  };

  const iS = { width: "100%", padding: "12px 14px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 14, fontFamily: "inherit", outline: "none" };
  const lS = { fontSize: 12, fontWeight: 600, color: C.textSoft, marginBottom: 6, display: "block" };
  const bS = { padding: 14, borderRadius: 12, border: "none", color: "#fff", fontWeight: 700, fontSize: 15, background: C.green, marginTop: 12, cursor: "pointer", width: "100%" };

  const Field = ({ label, value, onChange, ph, type = "text" }) => (
    <div style={{ marginBottom: 14 }}><label style={lS}>{label}</label><input type={type} placeholder={ph} value={value} onChange={e => onChange(e.target.value)} style={iS} /></div>
  );

  // ── CONFIGURAÇÃO DE TELA ──────────────────────────────────────────
  const CORES_ACCENT = [
    { cor: "#E8201A", nome: "Vermelho" },
    { cor: "#3B82F6", nome: "Azul" },
    { cor: "#8B5CF6", nome: "Roxo" },
    { cor: "#22C55E", nome: "Verde" },
    { cor: "#F97316", nome: "Laranja" },
    { cor: "#EC4899", nome: "Rosa" },
    { cor: "#06B6D4", nome: "Ciano" },
    { cor: "#FACC15", nome: "Amarelo" },
  ];

  const TEMAS = [
    { key: "noite", label: "Noite", icon: "🌙", desc: "Escuro profundo — padrão" },
    { key: "dia", label: "Dia", icon: "☀️", desc: "Claro e limpo" },
    { key: "escuro", label: "Ultra Escuro", icon: "⬛", desc: "Preto puro" },
    { key: "grafite", label: "Grafite", icon: "🔵", desc: "Azul escuro moderno" },
  ];

  const FONTES = [
    { key: "pequeno", label: "Pequeno", size: "12px" },
    { key: "normal", label: "Normal", size: "14px" },
    { key: "grande", label: "Grande", size: "16px" },
    { key: "extragrande", label: "Extra Grande", size: "18px" },
  ];

  const ConfigTela = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* MODO DIA / NOITE */}
      <div style={{ background: C.card, borderRadius: 16, padding: 24, border: `1px solid ${C.border}` }}>
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>Modo de Exibição</h3>
          <p style={{ fontSize: 13, color: C.textMuted }}>Escolha o tema visual do painel</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {TEMAS.map(t => {
            const active = themeCfg.preset === t.key;
            const prev = PRESETS[t.key];
            return (
              <div key={t.key} onClick={() => updateConfig({ preset: t.key })} style={{
                padding: "16px 18px", borderRadius: 14, cursor: "pointer", transition: "all .2s",
                border: active ? `2px solid ${C.red}` : `1px solid ${C.border}`,
                background: active ? `${C.red}12` : C.bg,
                display: "flex", alignItems: "center", gap: 14,
              }}>
                {/* mini preview do tema */}
                <div style={{ width: 44, height: 44, borderRadius: 10, background: prev.bg, border: `2px solid ${prev.border}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative" }}>
                  <div style={{ position: "absolute", left: 0, top: 0, width: 14, height: "100%", background: prev.sidebar }} />
                  <div style={{ marginLeft: 6, width: 22, height: 6, borderRadius: 3, background: prev.red }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: active ? C.text : C.textSoft, display: "flex", alignItems: "center", gap: 6 }}>
                    <span>{t.icon}</span> {t.label}
                  </div>
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{t.desc}</div>
                </div>
                {active && (
                  <div style={{ width: 20, height: 20, borderRadius: "50%", background: C.red, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#fff", fontWeight: 800 }}>✓</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* COR DE DESTAQUE */}
      <div style={{ background: C.card, borderRadius: 16, padding: 24, border: `1px solid ${C.border}` }}>
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>Cor de Destaque</h3>
          <p style={{ fontSize: 13, color: C.textMuted }}>Cor principal dos botões, badges e elementos ativos</p>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          {CORES_ACCENT.map(({ cor, nome }) => {
            const active = themeCfg.accentColor === cor;
            return (
              <div key={cor} onClick={() => updateConfig({ accentColor: cor })} title={nome} style={{
                width: 44, height: 44, borderRadius: 12, background: cor, cursor: "pointer", transition: "all .2s",
                border: active ? `3px solid ${C.text}` : "3px solid transparent",
                boxShadow: active ? `0 0 0 2px ${cor}` : "none",
                transform: active ? "scale(1.15)" : "scale(1)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {active && <span style={{ fontSize: 16, color: "#fff", fontWeight: 800, textShadow: "0 1px 3px rgba(0,0,0,.5)" }}>✓</span>}
              </div>
            );
          })}
        </div>
        {/* cor customizada */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.bg, borderRadius: 12, border: `1px solid ${C.border}` }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.textSoft, flex: 1 }}>Cor personalizada</label>
          <input type="color" value={themeCfg.accentColor} onChange={e => updateConfig({ accentColor: e.target.value })}
            style={{ width: 44, height: 36, borderRadius: 8, border: "none", cursor: "pointer", background: "transparent" }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: C.textMuted, fontFamily: "monospace" }}>{themeCfg.accentColor}</span>
        </div>
      </div>

      {/* TAMANHO DA FONTE */}
      <div style={{ background: C.card, borderRadius: 16, padding: 24, border: `1px solid ${C.border}` }}>
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>Tamanho da Fonte</h3>
          <p style={{ fontSize: 13, color: C.textMuted }}>Ajuste o tamanho do texto em todo o painel</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {FONTES.map(f => {
            const active = themeCfg.fontSize === f.key;
            return (
              <div key={f.key} onClick={() => updateConfig({ fontSize: f.key })} style={{
                flex: 1, padding: "16px 12px", borderRadius: 12, cursor: "pointer", textAlign: "center", transition: "all .2s",
                border: active ? `2px solid ${C.red}` : `1px solid ${C.border}`,
                background: active ? `${C.red}12` : C.bg,
              }}>
                <div style={{ fontSize: f.size, fontWeight: 700, color: active ? C.text : C.textSoft, marginBottom: 4 }}>Aa</div>
                <div style={{ fontSize: 11, color: active ? C.red : C.textMuted, fontWeight: 600 }}>{f.label}</div>
                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>{f.size}</div>
              </div>
            );
          })}
        </div>
        {/* preview de texto */}
        <div style={{ marginTop: 16, padding: "14px 16px", background: C.bg, borderRadius: 12, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.08em" }}>Preview</div>
          <div style={{ fontSize: ({ pequeno: 12, normal: 14, grande: 16, extragrande: 18 }[themeCfg.fontSize] || 14), color: C.text, fontWeight: 500 }}>
            MixUp — Painel do Lojista
          </div>
          <div style={{ fontSize: ({ pequeno: 10, normal: 12, grande: 13, extragrande: 15 }[themeCfg.fontSize] || 12), color: C.textMuted, marginTop: 4 }}>
            Pedidos, relatórios e configurações em um só lugar
          </div>
        </div>
      </div>

      {/* BOTÃO RESETAR */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button onClick={() => { updateConfig({ preset: "noite", accentColor: "#E8201A", fontSize: "normal" }); notify("Configurações resetadas!"); }}
          style={{ padding: "10px 22px", borderRadius: 10, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
          Resetar padrão
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ animation: "fadeUp .4s ease-out" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Plus Jakarta Sans'", fontWeight: 800, fontSize: 28 }}>{_("configuracao")}</h1>
      </div>

      {toast && <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 999, background: toast.ok ? C.green : C.red, color: "#fff", padding: "12px 28px", borderRadius: 12, fontWeight: 700, fontSize: 14, animation: "slideDown .3s ease-out" }}>{toast.msg}</div>}

      {/* CONFIGURAÇÃO DE TELA */}
      {sub === "configuracao_tela" && <ConfigTela />}

      {/* HORÁRIOS */}
      {sub === "horarios" && (
        <Card>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>{_("horariosFuncionamento")}</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {horarios.map((h, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 16px", background: C.bg, borderRadius: 12, border: `1px solid ${C.border}` }}>
                <span style={{ fontWeight: 700, fontSize: 14, width: 90 }}>{h.dia}</span>
                <div onClick={() => { const n = [...horarios]; n[i].aberto = !n[i].aberto; setHorarios(n); }}
                  style={{ width: 44, height: 24, borderRadius: 12, background: h.aberto ? C.green : C.border, cursor: "pointer", position: "relative", transition: "all .2s" }}>
                  <div style={{ width: 20, height: 20, borderRadius: 10, background: "#fff", position: "absolute", top: 2, left: h.aberto ? 22 : 2, transition: "all .2s" }} />
                </div>
                <span style={{ fontSize: 12, color: h.aberto ? C.greenLight : C.textMuted, fontWeight: 600, width: 60 }}>{h.aberto ? _("aberto") : _("fechado")}</span>
                {h.aberto && <>
                  <input type="time" value={h.abre} onChange={e => { const n = [...horarios]; n[i].abre = e.target.value; setHorarios(n); }} style={{ ...iS, width: 130 }} />
                  <span style={{ color: C.textMuted }}>—</span>
                  <input type="time" value={h.fecha} onChange={e => { const n = [...horarios]; n[i].fecha = e.target.value; setHorarios(n); }} style={{ ...iS, width: 130 }} />
                </>}
              </div>
            ))}
          </div>
          <button className="form-btn" onClick={() => notify(_("horariosAlvo"))} style={bS}>{_("salvarHorarios")}</button>
        </Card>
      )}

      {/* DADOS DA CONTA */}
      {sub === "dados_conta" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <Card>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>{_("informacoesLoja")}</h3>
            <Field label={`${_("nomeLoja")} *`} ph="Nome" value={fDados.nome} onChange={v => setFDados({ ...fDados, nome: v })} />
            <Field label="CNPJ" ph="00.000.000/0001-00" value={fDados.cnpj} onChange={v => setFDados({ ...fDados, cnpj: v })} />
            <Field label={_("telefone")} ph="(67) 99999-9999" value={fDados.telefone} onChange={v => setFDados({ ...fDados, telefone: v })} />
            <Field label={_("email")} ph="loja@email.com" value={fDados.email} onChange={v => setFDados({ ...fDados, email: v })} />
            <Field label={_("endereco")} ph="Rua, número" value={fDados.endereco} onChange={v => setFDados({ ...fDados, endereco: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label={_("cidade")} ph="Ponta Porã" value={fDados.cidade} onChange={v => setFDados({ ...fDados, cidade: v })} />
              <Field label={_("estado")} ph="MS" value={fDados.estado} onChange={v => setFDados({ ...fDados, estado: v })} />
            </div>
            <button className="form-btn" onClick={salvarDados} style={{ ...bS, opacity: saving ? .6 : 1 }} disabled={saving}>{saving ? _("salvando") : _("salvarDados")}</button>
          </Card>
          <Card>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>{_("informacoes")}</h3>
            <div style={{ padding: "16px", background: C.bg, borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8 }}>{_("idLoja")}</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>#{lojaId}</div>
            </div>
            <div style={{ padding: "14px 16px", background: `${C.blue}10`, borderRadius: 12, border: `1px solid ${C.blue}30` }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.blue }}>{_("taxasAdmin")}</div>
            </div>
          </Card>
        </div>
      )}

      {/* API PAGAMENTO */}
      {sub === "api_pagamento" && (
        <Card>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{_("integracaoMP")}</h3>
          <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 20 }}>{_("configCredenciais")}</p>
          <Field label={_("accessToken")} ph="APP_USR-..." value={fApi.mp_access_token} onChange={v => setFApi({ ...fApi, mp_access_token: v })} />
          <Field label={_("publicKey")} ph="APP_USR-..." value={fApi.mp_public_key} onChange={v => setFApi({ ...fApi, mp_public_key: v })} />
          <Field label={_("webhookUrl")} ph="https://mixupbr.com.br/api/webhook/" value={fApi.webhook_url} onChange={v => setFApi({ ...fApi, webhook_url: v })} />
          <div style={{ padding: "14px 16px", background: `${C.blue}10`, borderRadius: 12, border: `1px solid ${C.blue}30`, marginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.blue }}>{_("credenciaisInfo")}</div>
          </div>
          <button className="form-btn" onClick={() => notify(_("apiConfigurada"))} style={bS}>{_("salvarConfiguracao")}</button>
        </Card>
      )}

      {/* IMPRESSORA */}
      {sub === "impressora" && (
        <Card>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>{_("configImpressora")}</h3>
          <div style={{ marginBottom: 14 }}>
            <label style={lS}>{_("tipoConexao")}</label>
            <select value={fImp.tipo} onChange={e => setFImp({ ...fImp, tipo: e.target.value })} style={iS}>
              <option value="bluetooth">{_("bluetooth")}</option>
              <option value="usb">{_("usb")}</option>
              <option value="wifi">{_("wifiRede")}</option>
            </select>
          </div>
          <Field label={_("nomeDispositivo")} ph={fImp.tipo === "wifi" ? "192.168.1.100" : "Impressora-BT"} value={fImp.nome_dispositivo} onChange={v => setFImp({ ...fImp, nome_dispositivo: v })} />
          <div style={{ marginBottom: 14 }}>
            <label style={lS}>{_("larguraPapel")}</label>
            <select value={fImp.largura_papel} onChange={e => setFImp({ ...fImp, largura_papel: e.target.value })} style={iS}>
              <option value="58">58mm</option><option value="80">80mm</option>
            </select>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={lS}>{_("imprimirAuto")}</label>
            <select value={fImp.auto_imprimir} onChange={e => setFImp({ ...fImp, auto_imprimir: e.target.value })} style={iS}>
              <option value="1">{_("simImprimir")}</option><option value="0">{_("naoImprimir")}</option>
            </select>
          </div>
          <button className="form-btn" onClick={() => notify(_("impressoraConfigurada"))} style={bS}>{_("salvarImpressora")}</button>
        </Card>
      )}

      {/* TIPOS DE PAGAMENTO */}
      {sub === "tipos_pagamento" && (
        <Card>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{_("tiposPagAceitos")}</h3>
          <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 20 }}>{_("ativeDesative")}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {tiposPag.map((t, i) => (
              <div key={t.key} style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 20px", background: C.bg, borderRadius: 12, border: `1px solid ${C.border}` }}>
                <span style={{ fontWeight: 700, fontSize: 15, flex: 1 }}>{t.label}</span>
                <div onClick={() => { const n = [...tiposPag]; n[i].ativo = !n[i].ativo; setTiposPag(n); }}
                  style={{ width: 50, height: 28, borderRadius: 14, background: t.ativo ? C.green : C.border, cursor: "pointer", position: "relative", transition: "all .2s" }}>
                  <div style={{ width: 24, height: 24, borderRadius: 12, background: "#fff", position: "absolute", top: 2, left: t.ativo ? 24 : 2, transition: "all .2s", boxShadow: "0 2px 4px rgba(0,0,0,.2)" }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: t.ativo ? C.greenLight : C.textMuted, width: 50 }}>{t.ativo ? _("ativo") : _("inativo")}</span>
              </div>
            ))}
          </div>
          <button className="form-btn" onClick={() => notify(_("pagamentosAtualizados"))} style={bS}>{_("salvarPagamentos")}</button>
        </Card>
      )}

      {/* IDIOMAS */}
      {sub === "idiomas" && (
        <Card>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{_("idiomasTitulo")}</h3>
          <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 24 }}>{_("idiomasDesc")}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {LANGUAGES.map(l => (
              <div key={l.key} onClick={() => { setLang(l.key); notify(_("idiomaSalvo")); }}
                style={{
                  display: "flex", alignItems: "center", gap: 16, padding: "18px 22px",
                  background: lang === l.key ? `${C.red}12` : C.bg,
                  borderRadius: 14,
                  border: lang === l.key ? `2px solid ${C.red}` : `1px solid ${C.border}`,
                  cursor: "pointer", transition: "all .2s",
                }}>
                <span style={{ fontSize: 28 }}>{l.flag}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: lang === l.key ? C.text : C.textSoft }}>{l.label}</div>
                </div>
                {lang === l.key && (
                  <div style={{ padding: "4px 14px", borderRadius: 8, background: C.red, color: "#fff", fontSize: 12, fontWeight: 700 }}>
                    {_("idiomaAtual")}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
